import { Component, Input, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  host: {
    class: 'split-pane-main-content',
  },
  selector: 'split-pane-main-content',
  template: `
    <div class="split-pane-main-content__inner" [ngClass]="{ 'has-single-column': !secondaryContentVisible }">
      <div class="split-pane-main-col split-pane-main-col--1">
        <ng-content select=".split-pane-main-col1-content"></ng-content>
      </div>
      <div class="split-pane-main-col split-pane-main-col--2">
        <ng-content select=".split-pane-main-col2-content"></ng-content>
        @if (secondaryContentType === 'routerOutlet' && nestedOutletEnabled) {
          <ion-router-outlet (activate)="outletActivated()" (deactivate)="outletDeactivated()"></ion-router-outlet>
        }
      </div>
    </div>
  `,
})
export class SplitPaneMainContentComponent {
  @Input() public rootPath: string | undefined; // required for secondaryContentType = 'routerOutlet'
  @Input() public secondaryContentType: 'routerOutlet' | 'custom' | undefined;
  @Input() public secondaryContentVisible: boolean;

  public nestedOutletEnabled = true;

  private router = inject(Router);
  private ngUnsubscribe: Subject<void>;

  public ngOnInit(): void {
    this.ngUnsubscribe = new Subject<void>();

    this.router.events
      .pipe(takeUntil(this.ngUnsubscribe),
        filter((event) => { return event instanceof NavigationEnd; }))
      .subscribe((ev: NavigationEnd) => {
        if (ev instanceof NavigationEnd && ev.url) {
          // close detail view manually when route is no nested route (list + detail view)
          //
          // scenario:
          // (1) navigate to organizations/selfHelpAddress/all
          // (2) navigate to organizations/selfHelpAddress/all/<DETAIL_ID>
          // (3) navigate to organizations/selfHelpAddress/selfHelpAddressRa
          // (4) navigate to organizations/selfHelpAddress/all
          // detail view is still visible even though route is "organizations/selfHelpAddress/all"
          // + clicking again on "all" menu item doesn't close the detail view
          // which is probably due to ionics imperfect nested router implementation?
          //
          // we now always close the detail view when route is no nested route
          // history still works via browser back button
          const normalizedRoute = this.getNormalizedRoute(this.router.url);

          if (normalizedRoute.includes(this.rootPath) && !this.isNestedRoute(normalizedRoute) && this.secondaryContentVisible) {
            this.secondaryContentVisible = false;
          }
        }
      });
  }

  public outletActivated(): void {
    console.info('nested outlet activated for', this.rootPath);
    this.secondaryContentVisible = true;
  }

  public outletDeactivated(): void {
    const normalizedRoute = this.getNormalizedRoute(this.router.url);

    if (normalizedRoute.includes(this.rootPath) && !this.isNestedRoute(normalizedRoute)) {
      this.nestedOutletEnabled = false;
      this.secondaryContentVisible = false;
      requestAnimationFrame(() => {
        // we want to re-enable our router outlet immediately afterwards.
        // if we do it later (e.g in router events subscription), ionic will throw a stack container exception
        this.nestedOutletEnabled = true;
      });
    }
  }

  private isNestedRoute(normalizedRoute: string): boolean {
    return !!normalizedRoute && !normalizedRoute.endsWith(this.rootPath);
  }

  private getNormalizedRoute(route: string): string {
    if (!route) {
      return '';
    }

    return route.split('?')[0];
  }
}
